import { useEffect, useState, useCallback, useRef, lazy, Suspense } from "react";
import { Send, ChevronDown, ChevronUp, XCircle, AlertTriangle, Terminal, CheckCircle2, RefreshCw, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { PageHeader, Table, Td, StatusBadge, EmptyState } from "../../components/dashboard/Shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

// Lazy-loaded TradingView Advanced Chart — only fetches the widget script after page render
const TradingViewChart = lazy(() => import("../../components/TradingViewChart"));

const TV_SYMBOL_MAP = {
  XAUUSD: "OANDA:XAUUSD", XAUUSDM: "OANDA:XAUUSD", GOLD: "OANDA:XAUUSD",
  EURUSD: "OANDA:EURUSD", GBPUSD: "OANDA:GBPUSD", USDJPY: "OANDA:USDJPY",
  BTCUSD: "BINANCE:BTCUSDT", ETHUSD: "BINANCE:ETHUSDT",
  US30: "OANDA:US30USD", NAS100: "OANDA:NAS100USD",
};
const tvSymbolFor = (raw) => TV_SYMBOL_MAP[(raw || "XAUUSD").toUpperCase()] || `OANDA:${(raw || "XAUUSD").toUpperCase()}`;

const TYPES = [
  ["market_buy", "BUY MARKET"], ["market_sell", "SELL MARKET"],
  ["limit_buy", "BUY LIMIT"], ["limit_sell", "SELL LIMIT"],
  ["stop_buy", "BUY STOP"], ["stop_sell", "SELL STOP"],
];
const NEEDS_ENTRY = ["limit_buy", "limit_sell", "stop_buy", "stop_sell"];

const STEP_ORDER = ["received", "preflight", "metaapi_fetch", "connect", "resolve_symbol", "calc_lots", "send_order", "persisted"];
const STEP_LABELS = {
  received: "Received",
  preflight: "Preflight",
  metaapi_fetch: "MetaApi Account",
  connect: "Connect + Sync",
  resolve_symbol: "Resolve Symbol",
  calc_lots: "Lot Sizing",
  send_order: "Send Order",
  persisted: "Persisted",
};

function AccountExecCard({ exe }) {
  const [open, setOpen] = useState(false);
  const steps = exe.steps || [];
  const stepMap = {};
  steps.forEach((s) => { stepMap[s.code] = s; });
  const isSuccess = exe.status === "success";
  const isFailed = exe.status === "failed" || exe.status === "timeout";
  const isSkipped = exe.status === "skipped" || exe.status === "paused";
  const currentStepCode = steps.length > 0 ? steps[steps.length - 1].code : "received";
  const statusPill = isSuccess ? "bg-[#00E676]/15 text-[#00E676]" : isFailed ? "bg-[#FF3B30]/15 text-[#FF3B30]" : isSkipped ? "bg-amber-500/15 text-amber-400" : "bg-[#00F0FF]/15 text-[#00F0FF]";
  return (
    <div className="rounded border border-white/10 bg-black/40" data-testid={`exec-card-${exe.exec_id}`}>
      <div className="flex items-center gap-3 px-3 py-2.5 cursor-pointer" onClick={() => setOpen((o) => !o)}>
        {isSuccess ? <CheckCircle2 size={14} className="text-[#00E676] shrink-0" /> :
         isFailed ? <XCircle size={14} className="text-[#FF3B30] shrink-0" /> :
         isSkipped ? <AlertTriangle size={14} className="text-amber-400 shrink-0" /> :
         <Loader2 size={14} className="text-[#00F0FF] shrink-0 animate-spin" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-white truncate">{exe.client_name || exe.client_email || exe.user_id}</span>
            <span className="tabular text-[11px] text-gray-500">{exe.broker} · {exe.login}</span>
          </div>
          <div className="text-[10px] text-gray-500 mt-0.5">
            {isSuccess && `Order ${exe.order_id || "OK"} · ${exe.duration_ms ?? "—"} ms total${exe.latency?.connect_ms != null ? ` (connect ${exe.latency.connect_ms} · broker ${exe.latency.broker_ms})` : ""} · ${exe.lots ?? "—"} lots`}
            {isFailed && `${exe.status === "timeout" ? "TIMEOUT · " : ""}${(exe.error || "Failed").slice(0, 160)}`}
            {isSkipped && `${exe.status === "paused" ? "PAUSED · " : ""}${exe.error || "Skipped"}`}
            {!isSuccess && !isFailed && !isSkipped && `${STEP_LABELS[currentStepCode] || currentStepCode}…`}
          </div>
        </div>
        <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded font-semibold ${statusPill}`}>{exe.status}</span>
        {open ? <ChevronUp size={13} className="text-gray-600" /> : <ChevronDown size={13} className="text-gray-600" />}
      </div>
      {open && (
        <div className="border-t border-white/5 px-3 py-2 space-y-1">
          {STEP_ORDER.map((code) => {
            const s = stepMap[code];
            const reached = !!s;
            const failed = s && s.ok === false;
            return (
              <div key={code} className="flex items-start gap-2 text-[11px]" data-testid={`step-${exe.exec_id}-${code}`}>
                <span className={`mt-0.5 w-1.5 h-1.5 rounded-full shrink-0 ${failed ? "bg-[#FF3B30]" : reached ? "bg-[#00E676]" : "bg-gray-700"}`} />
                <span className={`w-28 shrink-0 ${reached ? "text-gray-300" : "text-gray-600"}`}>{STEP_LABELS[code] || code}</span>
                <span className={`flex-1 ${failed ? "text-[#FF3B30]" : reached ? "text-gray-400" : "text-gray-700"}`}>
                  {s ? s.message : "—"}
                </span>
                {s?.ts && <span className="text-gray-700 tabular text-[10px]">{new Date(s.ts).toLocaleTimeString()}</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ExecutionStatusModal({ signalId, onClose }) {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/signals/${signalId}/status`);
        if (cancelled) return;
        setStatus(data);
        if (data.signal?.status === "completed") {
          clearInterval(pollRef.current);
        }
      } catch (e) {
        setError(e?.response?.data?.detail || "Failed to fetch status");
      }
    };
    tick();
    pollRef.current = setInterval(tick, 1000);
    return () => { cancelled = true; clearInterval(pollRef.current); };
  }, [signalId]);

  const sig = status?.signal;
  const execs = status?.executions || [];
  const totals = sig?.totals || null;
  const preflight = sig?.preflight || [];
  const connected = sig?.targets_count ?? preflight.length;
  const eligibleCount = preflight.filter((p) => p.eligible).length;
  const running = sig?.status !== "completed";

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0A0A0A] border border-white/10 text-gray-200 max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="exec-status-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-white flex items-center gap-2">
            {running ? <Loader2 size={16} className="text-[#00F0FF] animate-spin" /> : <CheckCircle2 size={16} className="text-[#00E676]" />}
            Execution Status · {sig?.type?.replace("_", " ").toUpperCase()} {sig?.symbol}
          </DialogTitle>
        </DialogHeader>
        {error && <p className="text-xs text-[#FF3B30]" data-testid="exec-status-error">{error}</p>}
        {!status ? (
          <div className="p-4"><div className="skeleton h-24" /></div>
        ) : (
          <div className="overflow-y-auto pr-1 space-y-4 flex-1">
            {/* Header stats */}
            <div className="grid grid-cols-4 gap-3">
              <div className="rounded bg-black/40 p-2.5" data-testid="stat-connected">
                <p className="text-[9px] uppercase tracking-widest text-gray-500">Connected</p>
                <p className="text-lg text-white tabular">{connected}</p>
              </div>
              <div className="rounded bg-black/40 p-2.5" data-testid="stat-eligible">
                <p className="text-[9px] uppercase tracking-widest text-gray-500">Eligible</p>
                <p className={`text-lg tabular ${eligibleCount > 0 ? "text-[#00F0FF]" : "text-[#FF3B30]"}`}>{eligibleCount}</p>
              </div>
              <div className="rounded bg-black/40 p-2.5" data-testid="stat-success">
                <p className="text-[9px] uppercase tracking-widest text-gray-500">Success</p>
                <p className="text-lg text-[#00E676] tabular">{totals?.success ?? "…"}</p>
              </div>
              <div className="rounded bg-black/40 p-2.5" data-testid="stat-failed">
                <p className="text-[9px] uppercase tracking-widest text-gray-500">Failed</p>
                <p className="text-lg text-[#FF3B30] tabular">{totals?.failed ?? "…"}</p>
              </div>
            </div>

            {/* Preflight — who was targeted */}
            {preflight.length > 0 && (
              <div>
                <p className="label-xs mb-2">Preflight — targeted accounts</p>
                <div className="rounded border border-white/10 bg-black/30 divide-y divide-white/5 text-[11px]" data-testid="preflight-list">
                  {preflight.map((p, i) => (
                    <div key={i} className="flex items-center gap-2 px-3 py-1.5">
                      <span className={`w-1.5 h-1.5 rounded-full ${p.eligible ? "bg-[#00E676]" : "bg-amber-500"}`} />
                      <span className="text-white">{p.client_name || p.client_email}</span>
                      <span className="tabular text-gray-500">{p.broker} · {p.login}</span>
                      <span className="tabular text-gray-600">{p.has_metaapi_id ? "" : "· NO metaapi_id"}</span>
                      <span className={`ml-auto ${p.eligible ? "text-[#00E676]" : "text-amber-400"}`}>{p.reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Per-account executions with step trace */}
            <div>
              <p className="label-xs mb-2">Per-account execution ({execs.length})</p>
              {execs.length === 0 && !running && (
                <p className="text-xs text-gray-500 py-4">
                  No accounts were reached — 0 connected MT5 accounts. Ask your investors to connect their MT5 accounts first.
                </p>
              )}
              {execs.length === 0 && running && (
                <div className="flex items-center gap-2 text-xs text-gray-500 py-2">
                  <Loader2 size={12} className="animate-spin" /> Preparing fan-out to eligible accounts…
                </div>
              )}
              <div className="space-y-2">
                {execs.map((e) => <AccountExecCard key={e.exec_id} exe={e} />)}
              </div>
            </div>

            {!running && (
              <p className="text-[11px] text-gray-500 pt-2 border-t border-white/10" data-testid="exec-completed-summary">
                Completed · {totals?.success || 0} succeeded · {totals?.failed || 0} failed · {totals?.skipped || 0} skipped across {totals?.targets || 0} accounts.
              </p>
            )}
          </div>
        )}
        <div className="pt-3 flex justify-end">
          <button onClick={onClose} data-testid="exec-status-close" className="btn-outline px-4 py-1.5 text-xs flex items-center gap-1.5">
            <X size={12} /> Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ExecutionRows({ signalId }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get(`/signals/${signalId}/executions`).then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, [signalId]);
  if (!rows) return <div className="skeleton h-16 my-2" />;
  if (rows.length === 0) return <p className="text-xs text-gray-500 py-3">No connected client accounts were targeted.</p>;
  return (
    <div className="my-2">
      <Table headers={["Client", "Account", "Lots", "Status", "Order/Error", "ms"]} testId={`exec-table-${signalId}`}>
        {rows.map((e) => (
          <tr key={e.exec_id} className="border-b border-white/5">
            <Td className="text-xs">{e.client_name || e.user_id}</Td>
            <Td className="tabular text-xs">{e.broker} · {e.login}</Td>
            <Td className="tabular text-xs">{e.lots ?? "—"}</Td>
            <Td><StatusBadge status={e.status === "success" ? "paid" : e.status === "failed" ? "overdue" : "pending"} />{" "}<span className="text-[10px] text-gray-500">{e.status}</span></Td>
            <Td className="text-xs !whitespace-normal max-w-sm text-gray-500">{e.status === "success" ? (e.order_id || "OK") : (e.error || "").slice(0, 160)}</Td>
            <Td className="tabular text-xs text-gray-600">{e.duration_ms ?? "—"}</Td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

export default function Signals() {
  const [signals, setSignals] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [form, setForm] = useState({ type: "market_buy", symbol: "", entry: "", sl: "", tp: "", risk_pct: "", comment: "" });
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [closeModal, setCloseModal] = useState(null); // { mode: 'all' | 'symbol', symbol: '' }
  const [closeBusy, setCloseBusy] = useState(false);
  const [debug, setDebug] = useState([]);   // step-by-step console trace
  const [debugTrace, setDebugTrace] = useState(null);  // backend eligibility trace
  const [lastMarker, setLastMarker] = useState(null);  // chart marker after send / close
  const [activeSignalId, setActiveSignalId] = useState(null);  // opens live execution status modal

  const pushDebug = (level, msg, extra) => {
    setDebug((d) => [...d, {
      ts: new Date().toLocaleTimeString(),
      level, msg, extra
    }].slice(-50));
  };

  const loadDebugTrace = useCallback(async () => {
    try {
      const { data } = await api.get("/signals/debug/trace");
      setDebugTrace(data);
    } catch (e) {
      pushDebug("error", "Failed to fetch debug trace", e?.response?.data);
    }
  }, []);

  const load = useCallback(() => {
    api.get("/signals").then(({ data }) => setSignals(data)).catch(() => setSignals([]));
    api.get("/signals/readiness").then(({ data }) => setReadiness(data)).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [load]);

  const submit = async (e, overrideType = null) => {
    if (e && e.preventDefault) e.preventDefault();
    setDebug([]);
    pushDebug("info", "Button clicked");
    const effectiveType = overrideType || form.type;
    if (!form.symbol) {
      pushDebug("error", "Validation failed: symbol missing");
      toast.error("Symbol is required");
      return;
    }
    if (NEEDS_ENTRY.includes(effectiveType) && !form.entry) {
      pushDebug("error", `Validation failed: entry required for ${effectiveType}`);
      toast.error("Entry price required for limit/stop orders");
      return;
    }
    pushDebug("ok", `Frontend validation passed (${effectiveType} ${form.symbol})`);
    const label = TYPES.find((t) => t[0] === effectiveType)?.[1] || effectiveType;
    if (!window.confirm(`Send ${label} ${form.symbol.toUpperCase()} signal to ALL connected client accounts?`)) {
      pushDebug("warn", "Cancelled by manager (confirm dialog dismissed)");
      return;
    }
    setBusy(true);
    try {
      const payload = { type: effectiveType, comment: form.comment || null, symbol: form.symbol, client_sent_at: new Date().toISOString() };
      if (form.entry && !overrideType) payload.entry = parseFloat(form.entry);
      if (form.sl) payload.sl = parseFloat(form.sl);
      if (form.tp) payload.tp = parseFloat(form.tp);
      if (form.risk_pct) payload.risk_pct = parseFloat(form.risk_pct);
      pushDebug("info", "POST /api/signals", payload);
      const { data } = await api.post("/signals", payload);
      pushDebug("ok", `Signal created (id=${data.signal_id})`);
      pushDebug("info", `Backend dispatched fan-out — ${data.connected_accounts ?? "?"} connected accounts targeted`);
      // Open the live Execution Status modal immediately
      setActiveSignalId(data.signal_id);
      toast.success(`${label} dispatched — live status opening…`);
      const dir = effectiveType.includes("buy") ? "BUY" : effectiveType.includes("sell") ? "SELL" : null;
      if (dir) {
        setLastMarker({
          direction: dir,
          entry: form.entry ? parseFloat(form.entry) : null,
          sl: form.sl ? parseFloat(form.sl) : null,
          tp: form.tp ? parseFloat(form.tp) : null,
          ts: Date.now(),
        });
      }
      setForm({ ...form, comment: "" });
      // Poll executions in 2s to show result in debug
      setTimeout(async () => {
        try {
          const { data: execs } = await api.get(`/signals/${data.signal_id}/executions`);
          if (execs.length === 0) {
            pushDebug("warn", "Backend accepted signal but NO client accounts were eligible → 0 executions");
            pushDebug("info", "Fix: enable Dev Mode on Claude Signals page OR complete KYC + auto-trading-authorized + admin-approval for the target client");
          } else {
            execs.forEach((e) => {
              const level = e.status === "success" ? "ok" : e.status === "failed" ? "error" : "warn";
              pushDebug(level, `${e.client_name || e.user_id} · ${e.login}: ${e.status}${e.order_id ? ` (order ${e.order_id})` : ""}${e.error ? ` — ${e.error}` : ""}`);
            });
          }
        } catch (ex) {
          pushDebug("error", "Could not fetch executions", ex?.response?.data);
        }
      }, 2500);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      const status = err.response?.status;
      pushDebug("error", `Backend rejected: HTTP ${status || "?"} — ${typeof d === "string" ? d : JSON.stringify(d)}`);
      toast.error(typeof d === "string" ? d : "Failed to send signal");
    } finally { setBusy(false); }
  };

  const submitClose = async () => {
    if (!closeModal) return;
    const isAll = closeModal.mode === "all";
    if (!isAll && !closeModal.symbol) return toast.error("Enter a symbol");
    setCloseBusy(true);
    try {
      const payload = isAll
        ? { type: "close_all", comment: closeModal.comment || "Manager close all" }
        : { type: "close_trade", symbol: closeModal.symbol.toUpperCase(), comment: closeModal.comment || `Manager close ${closeModal.symbol.toUpperCase()}` };
      const { data } = await api.post("/signals", payload);
      setActiveSignalId(data.signal_id);
      toast.success(isAll ? "Close ALL dispatched — live status opening…" : `Close ${closeModal.symbol.toUpperCase()} dispatched`);
      // Chart close marker
      setLastMarker({
        direction: "CLOSE",
        entry: null, sl: null, tp: null,
        ts: Date.now(),
      });
      setCloseModal(null);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to send close signal");
    } finally { setCloseBusy(false); }
  };

  return (
    <div>
      <PageHeader title="Signals" sub="Risk Engine → Lot Calculator → Execution Queue → all connected MT5 accounts" />

      {readiness && (
        <div className="card-dark p-5 mb-6" data-testid="readiness-panel">
          <div className="flex flex-wrap items-center gap-4 mb-3">
            <p className="label-xs">Execution Readiness</p>
            <span className="tabular text-sm"><span className="text-[#00E676]">{readiness.eligible}</span><span className="text-gray-500"> / {readiness.total_connected} connected accounts eligible</span></span>
            <button onClick={loadDebugTrace} data-testid="open-debug-trace"
                    className="ml-auto text-[11px] text-[#00F0FF] hover:underline flex items-center gap-1">
              <Terminal size={12} /> Open debug trace
            </button>
          </div>
          {readiness.accounts.length === 0 ? (
            <p className="text-xs text-gray-500">No connected MT5 accounts yet — signals will have no targets.</p>
          ) : (
            <div className="space-y-2">
              {readiness.accounts.map((r) => (
                <div key={r.login} className="flex flex-wrap items-center gap-3 text-xs border-b border-white/5 pb-2" data-testid={`readiness-${r.login}`}>
                  <span className={`w-2 h-2 rounded-full shrink-0 ${r.eligible ? "bg-[#00E676]" : "bg-[#FF3B30]"}`} />
                  <span className="text-white">{r.client}</span>
                  <span className="tabular text-gray-500">{r.broker} · {r.login}</span>
                  <span className="tabular text-gray-400">eq {r.equity != null ? `$${Number(r.equity).toLocaleString()}` : "—"}</span>
                  <span className={`ml-auto ${r.eligible ? "text-[#00E676]" : "text-[#FFD600]"}`}>{r.reason}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Debug Console — shows step-by-step trace of the send-signal flow */}
      {(debug.length > 0 || debugTrace) && (
        <div className="card-dark p-5 mb-6 border border-[#00F0FF]/20" data-testid="debug-console">
          <div className="flex items-center justify-between mb-3">
            <p className="font-display text-white flex items-center gap-2">
              <Terminal size={14} className="text-[#00F0FF]" /> Signal Debug Console
            </p>
            <div className="flex items-center gap-2">
              <button onClick={loadDebugTrace} className="text-[11px] text-[#00F0FF] flex items-center gap-1 hover:underline" data-testid="debug-refresh">
                <RefreshCw size={11} /> Refresh
              </button>
              <button onClick={() => { setDebug([]); setDebugTrace(null); }} className="text-[11px] text-gray-500 hover:text-white">Clear</button>
            </div>
          </div>
          {debugTrace && (
            <div className="mb-3 p-3 rounded bg-black/40 text-[11px] font-mono space-y-1">
              <div className="flex gap-4">
                <span>backend_ok: <span className="text-[#00F0FF]">{String(debugTrace.backend_ok)}</span></span>
                <span>metaapi_configured: <span className="text-[#00F0FF]">{String(debugTrace.metaapi_configured)}</span></span>
                <span>dev_mode: <span className={debugTrace.dev_mode ? "text-[#7000FF]" : "text-gray-500"}>{String(debugTrace.dev_mode)}</span></span>
                <span>accounts: <span className="tabular">{debugTrace.total_accounts}</span></span>
                <span>connected: <span className="tabular">{debugTrace.connected}</span></span>
                <span>eligible: <span className={debugTrace.eligible > 0 ? "text-[#00F0FF]" : "text-[#FF3B30]"}>{debugTrace.eligible}</span></span>
              </div>
              <div className="text-gray-400 pt-1 border-t border-white/5">→ {debugTrace.next_step}</div>
              {debugTrace.trace && debugTrace.trace.length > 0 && (
                <table className="w-full text-[10px] mt-2">
                  <thead className="text-gray-500"><tr><th className="text-left">Client</th><th>Login</th><th>Conn</th><th>Trading</th><th>KYC</th><th>Admin</th><th>Eligible</th><th className="text-left pl-2">Reason</th></tr></thead>
                  <tbody>
                    {debugTrace.trace.map((t, i) => (
                      <tr key={i} className="border-t border-white/5">
                        <td>{t.client}</td>
                        <td className="tabular">{t.login}</td>
                        <td>{t.connected ? "✓" : "✗"}</td>
                        <td>{t.trading_enabled ? "✓" : "✗"}</td>
                        <td>{t.kyc}</td>
                        <td>{t.admin_approval}</td>
                        <td className={t.eligible ? "text-[#00F0FF]" : "text-[#FF3B30]"}>{t.eligible ? "YES" : "NO"}</td>
                        <td className="pl-2 text-gray-400">{t.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
          {debug.length > 0 && (
            <div className="p-3 rounded bg-black/60 text-[11px] font-mono space-y-0.5 max-h-56 overflow-y-auto">
              {debug.map((d, i) => (
                <div key={i} className="flex gap-2">
                  <span className="text-gray-600 shrink-0">{d.ts}</span>
                  <span className={
                    d.level === "ok" ? "text-[#00F0FF]" :
                    d.level === "error" ? "text-[#FF3B30]" :
                    d.level === "warn" ? "text-amber-400" :
                    "text-gray-300"
                  }>
                    [{d.level.toUpperCase()}]
                  </span>
                  <span className="text-gray-200 flex-1">{d.msg}</span>
                  {d.extra && <span className="text-gray-600 text-[10px]">{typeof d.extra === "string" ? d.extra : JSON.stringify(d.extra).slice(0, 100)}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="space-y-4">
          <form onSubmit={submit} className="card-dark p-6 space-y-4" data-testid="signal-form">
            <div>
              <div className="grid grid-cols-2 gap-3 mb-1">
                <button type="button" data-testid="quick-buy-now"
                        disabled={busy || !form.symbol}
                        onClick={() => submit(null, "market_buy")}
                        className="py-3.5 rounded-lg text-white font-semibold text-sm tracking-wide bg-gradient-to-b from-[#00E676] to-[#00B248] hover:from-[#00F888] hover:to-[#00C853] shadow-[0_6px_20px_rgba(0,230,118,0.35)] disabled:opacity-40 disabled:cursor-not-allowed transition-all">
                  ▲ BUY NOW
                </button>
                <button type="button" data-testid="quick-sell-now"
                        disabled={busy || !form.symbol}
                        onClick={() => submit(null, "market_sell")}
                        className="py-3.5 rounded-lg text-white font-semibold text-sm tracking-wide bg-gradient-to-b from-[#FF3B30] to-[#C41F14] hover:from-[#FF5449] hover:to-[#D32F1F] shadow-[0_6px_20px_rgba(255,59,48,0.35)] disabled:opacity-40 disabled:cursor-not-allowed transition-all">
                  ▼ SELL NOW
                </button>
              </div>
              <p className="text-[10px] text-gray-500 mb-2">Quick-fire market orders using current price · uses SL/TP/Risk from the form below</p>
              <label className="label-xs block mb-2">Signal Type</label>
              <select data-testid="signal-type-select" className="input-dark" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="label-xs block mb-2">Symbol</label>
              <input data-testid="signal-symbol-input" required className="input-dark uppercase" placeholder="EURUSD / XAUUSD" value={form.symbol} onChange={(e) => setForm({ ...form, symbol: e.target.value.toUpperCase() })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-xs block mb-2">Entry {NEEDS_ENTRY.includes(form.type) ? "(required)" : "(market)"}</label>
                <input data-testid="signal-entry-input" type="number" step="any" min="0" required={NEEDS_ENTRY.includes(form.type)} className="input-dark" placeholder="—" value={form.entry} onChange={(e) => setForm({ ...form, entry: e.target.value })} />
              </div>
              <div>
                <label className="label-xs block mb-2">Risk %</label>
                <input data-testid="signal-risk-input" type="number" step="0.1" min="0.1" max="20" className="input-dark" placeholder="1.0" value={form.risk_pct} onChange={(e) => setForm({ ...form, risk_pct: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-xs block mb-2">Stop Loss</label>
                <input data-testid="signal-sl-input" type="number" step="any" min="0" className="input-dark" placeholder="—" value={form.sl} onChange={(e) => setForm({ ...form, sl: e.target.value })} />
              </div>
              <div>
                <label className="label-xs block mb-2">Take Profit</label>
                <input data-testid="signal-tp-input" type="number" step="any" min="0" className="input-dark" placeholder="—" value={form.tp} onChange={(e) => setForm({ ...form, tp: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="label-xs block mb-2">Comment</label>
              <input data-testid="signal-comment-input" className="input-dark" maxLength={100} placeholder="Optional trade comment" value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} />
            </div>
            <button data-testid="signal-send-button" disabled={busy} className="btn-primary w-full py-2.5 text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50">
              <Send size={15} /> {busy ? "Dispatching…" : "Send Signal"}
            </button>
            <p className="text-[11px] text-gray-600 leading-relaxed">Executes on every connected, KYC-approved, authorized account with active trading status. Risk limits and lot mode are applied per client.</p>
          </form>

          <div className="card-dark p-5 border-[#FF3B30]/40" data-testid="close-actions-card">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle size={15} className="text-[#FF3B30]" />
              <p className="label-xs text-[#FF3B30]">Emergency Close Actions</p>
            </div>
            <p className="text-[11px] text-gray-500 mb-4 leading-relaxed">
              Close trades before TP/SL is hit. Broadcasted instantly to every connected client account via MetaApi.
            </p>
            <div className="space-y-3">
              <button
                data-testid="close-by-symbol-button"
                onClick={() => setCloseModal({ mode: "symbol", symbol: "", comment: "" })}
                className="w-full btn-outline py-2.5 text-sm font-medium flex items-center justify-center gap-2 text-[#FFD600] border-[#FFD600]/30 hover:bg-[#FFD600]/5"
              >
                <XCircle size={15} /> Close Position by Symbol
              </button>
              <button
                data-testid="close-all-clients-button"
                onClick={() => setCloseModal({ mode: "all", symbol: "", comment: "" })}
                className="w-full py-2.5 text-sm font-medium rounded flex items-center justify-center gap-2 bg-[#FF3B30]/10 border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/15 transition-colors"
              >
                <XCircle size={15} /> Close ALL Positions (Every Client)
              </button>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2" data-testid="chart-panel">
          <Suspense fallback={<div className="card-dark p-3 h-[620px] skeleton" />}>
            <TradingViewChart
              symbol={tvSymbolFor(form.symbol)}
              interval="15"
              height={620}
              lastMarker={lastMarker}
            />
          </Suspense>
        </div>
      </div>

      {/* Signal history moved below the 2-column grid — preserves per-client execution visibility */}
      <div className="mt-6" data-testid="signals-history-below">
        <p className="label-xs mb-3">Signal History</p>
        {!signals ? <div className="skeleton h-96" /> : signals.length === 0 ? (
          <EmptyState title="No signals sent yet" sub="Your dispatched signals and their per-account execution results will appear here." />
        ) : (
          <div className="space-y-3" data-testid="signals-list">
            {signals.map((s) => (
              <div key={s.signal_id} className="card-dark p-4">
                <div className="flex flex-wrap items-center gap-3" data-testid={`signal-row-${s.signal_id}`}>
                  <span className={`text-xs font-medium px-2 py-1 rounded ${s.type.includes("buy") ? "bg-[#00E676]/10 text-[#00E676]" : s.type.includes("sell") ? "bg-[#FF3B30]/10 text-[#FF3B30]" : "bg-[#FFD600]/10 text-[#FFD600]"}`}>
                    {s.type.replace("_", " ").toUpperCase()}
                  </span>
                  <span className="tabular text-white text-sm">{s.symbol || "ALL"}</span>
                  {s.entry && <span className="tabular text-xs text-gray-400">@ {s.entry}</span>}
                  {s.sl && <span className="tabular text-xs text-[#FF3B30]">SL {s.sl}</span>}
                  {s.tp && <span className="tabular text-xs text-[#00E676]">TP {s.tp}</span>}
                  <button onClick={() => setActiveSignalId(s.signal_id)} data-testid={`view-status-${s.signal_id}`} className="text-[10px] px-2 py-0.5 rounded bg-[#00F0FF]/10 text-[#00F0FF] hover:bg-[#00F0FF]/20">View live status</button>
                  <span className="ml-auto flex items-center gap-3 cursor-pointer" onClick={() => setExpanded(expanded === s.signal_id ? null : s.signal_id)}>
                    {s.totals ? (
                      <span className="text-xs tabular"><span className="text-[#00E676]">{s.totals.success}✓</span> <span className="text-[#FF3B30]">{s.totals.failed}✗</span> <span className="text-gray-500">{s.totals.skipped} skipped</span></span>
                    ) : (
                      <span className="text-xs text-[#00F0FF] animate-pulse">executing…</span>
                    )}
                    <span className="text-[11px] text-gray-600 tabular">{String(s.created_at).slice(5, 16).replace("T", " ")}</span>
                    {expanded === s.signal_id ? <ChevronUp size={14} className="text-gray-500" /> : <ChevronDown size={14} className="text-gray-500" />}
                  </span>
                </div>
                {expanded === s.signal_id && <ExecutionRows signalId={s.signal_id} />}
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!closeModal} onOpenChange={(o) => !o && !closeBusy && setCloseModal(null)}>
        <DialogContent className="bg-[#121212] border border-white/10 text-gray-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-white flex items-center gap-2">
              <AlertTriangle size={16} className="text-[#FF3B30]" />
              {closeModal?.mode === "all" ? "Close ALL Client Positions?" : "Close Positions by Symbol"}
            </DialogTitle>
          </DialogHeader>
          {closeModal && (
            <div className="space-y-4">
              <p className="text-sm text-gray-400 leading-relaxed">
                {closeModal.mode === "all"
                  ? "This will close every open position on every connected, eligible client MT5 account, immediately at market price. This action cannot be undone."
                  : "This will close every open position matching the given symbol on every connected client MT5 account, at market price."}
              </p>
              {closeModal.mode === "symbol" && (
                <div>
                  <label className="label-xs block mb-2">Symbol</label>
                  <input
                    data-testid="close-symbol-input"
                    autoFocus
                    className="input-dark uppercase"
                    placeholder="EURUSD / XAUUSD"
                    value={closeModal.symbol}
                    onChange={(e) => setCloseModal({ ...closeModal, symbol: e.target.value.toUpperCase() })}
                  />
                </div>
              )}
              <div>
                <label className="label-xs block mb-2">Comment (optional)</label>
                <input
                  data-testid="close-comment-input"
                  className="input-dark"
                  maxLength={80}
                  placeholder="Reason for close"
                  value={closeModal.comment || ""}
                  onChange={(e) => setCloseModal({ ...closeModal, comment: e.target.value })}
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  data-testid="close-cancel-button"
                  onClick={() => setCloseModal(null)}
                  disabled={closeBusy}
                  className="flex-1 btn-outline py-2.5 text-sm disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  data-testid="close-confirm-button"
                  onClick={submitClose}
                  disabled={closeBusy || (closeModal.mode === "symbol" && !closeModal.symbol)}
                  className="flex-1 py-2.5 text-sm font-medium rounded bg-[#FF3B30] text-white hover:bg-[#FF3B30]/90 disabled:opacity-50"
                >
                  {closeBusy ? "Dispatching…" : "Confirm Close"}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {activeSignalId && (
        <ExecutionStatusModal signalId={activeSignalId} onClose={() => { setActiveSignalId(null); load(); }} />
      )}
    </div>
  );
}
